// Library Methods
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import liveUpdateScreenImg from "src/assets/images/operating-manual/portal-overview/live-update-screen.png";
import liveUpdateScreenFrImg from "src/assets/images/operating-manual/portal-overview/live-update-screen-fr.png";
import liveUpdateGraphImg from "src/assets/images/operating-manual/portal-overview/live-update-graph.png";
import liveUpdateGraphFrImg from "src/assets/images/operating-manual/portal-overview/live-update-graph-fr.png";
import liveUpdatePatientViewImg from "src/assets/images/operating-manual/portal-overview/live-update-patient-view.png";
import liveUpdatePatientViewFrImg from "src/assets/images/operating-manual/portal-overview/live-update-patient-view-fr.png";

// Config
import { priorityLanguageMap } from "src/config/languages";

const LiveUpdatePage = () => {
  const { t } = useTranslation("operating-manual");

  const currentLang = i18next.language as keyof typeof priorityLanguageMap;

  const portalText = [
    {
      title: t("Maximize / Minimize Button"),
      content: t("Maximize or minimize the Live Update Panel."),
    },
    {
      title: t("Recenter"),
      content: t(
        "Press this button once to recenter the scene in virtual reality."
      ),
    },
    {
      title: t("Controller Mute Button"),
      content: t("Disables controller beep sounds."),
    },
    {
      title: t("Disable Voice Prompts"),
      content: t("Disables specific voice prompts."),
    },
    {
      title: t("Disable Captions"),
      content: t(
        "Disables caption visibility anytime a voice prompt is played."
      ),
    },
    {
      title: t("Volume Slider"),
      content: t("Controls headset volume."),
    },
    {
      title: t("Exam Information"),
      content: "",
    },
    {
      title: t("Exam Stage"),
      content: t(
        "Dark blue is complete, orange is in progress, white is not started."
      ),
    },
    {
      title: t("Progress Bar"),
      content: "",
    },
    {
      title: t("Eye Tracking Summary"),
      content: t(
        "A spike above the line means the patient was not fixated. A spike below means the patient was blinking."
      ),
    },
    {
      title: t("Grid"),
      content: t(
        "The green point is currently being tested. The orange point still requires more testing before a final result is calculated. A number / output is the final result for that grid location."
      ),
    },
    {
      title: t("False Positives"),
      content: t(
        "A high number here likely means that the patient is trigger happy."
      ),
    },
    {
      title: t("False Negatives"),
      content: t(
        "A high number here means that the patient is likely not pressing when they see a stimulus."
      ),
    },
    {
      title: t("Fixation Loss Percentage"),
      content: t(
        "Percentage of time the patient was not fixated during the exam."
      ),
    },
    {
      title: t("Play / Pause Button"),
      content: t("Press to play or pause the exam."),
    },
    {
      title: t("Restart"),
      content: t("Press to restart the exam."),
    },
    {
      title: t("Terminate"),
      content: t("Press to stop the exam."),
    },
    {
      title: t("VR Mode / Test Mode"),
      content: t("Switch between test mode view and VR mode view."),
    },
    {
      title: t("VR Screen"),
      content: t(
        "Live view of patient point of view in virtual reality. Only available in VR mode."
      ),
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Typography>{t("To enter the live update page,")}</Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Typography>
          {t("1. The HMD must be on and connected to Wi-Fi")}
        </Typography>
        <Typography>{t("2. An exam must be linked")}</Typography>
      </Box>

      <Typography>
        {t(
          "Locate the device card on the patient or dashboard page, and press on the screen to open the live update panel:"
        )}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <img
          src={
            currentLang === "fr" ? liveUpdateScreenFrImg : liveUpdateScreenImg
          }
          width="50%"
          alt="live-update-screen"
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <img
          src={currentLang === "fr" ? liveUpdateGraphFrImg : liveUpdateGraphImg}
          width="80%"
          alt="live-update-graph"
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <img
          src={
            currentLang === "fr"
              ? liveUpdatePatientViewFrImg
              : liveUpdatePatientViewImg
          }
          width="100%"
          alt="live-update-patient-view"
        />
      </Box>

      <Box
        sx={{
          padding: "20px",
          borderRadius: "8px",
          backgroundColor: "#5B6D7A30",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {portalText.map((portalItem, index) => (
            <Grid
              container
              key={portalItem.title}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Typography fontSize="14px" fontWeight={600}>
                {index + 1}. {portalItem.title}
              </Typography>
              <Typography fontSize="14px">{portalItem.content}</Typography>
            </Grid>
          ))}
        </Box>
      </Box>

      <Typography>
        {t("To learn more about portal functionality, see ")}
        <Link
          to="/knowledge-hub/operating-manual/appendix-f"
          target="_blank"
          style={{ color: "#0066CC", textDecoration: "underline" }}
        >
          {t("Appendix F - Portal Icon Glossary")}
        </Link>
      </Typography>
    </Box>
  );
};

export default LiveUpdatePage;
